import { readMachineStatus } from 'tf-pulse-shared';
import { IS_DEMO } from '../../demo/demoMode';
import { Machine as GeneratedMachine } from '../../gql/generated';
import { MachineStatusBadge } from '../../pages/machines/MachineStatusBadge';
import { getMachineStatusTone } from '../../pages/machines/machineStatusTone';
import { cn } from '../../utils/className';
import { APP_BAR_TONES } from '../appBarTones';
import { SidePanel } from '../SidePanel';
import { MachineDetailPane } from './MachineDetailPane';
import { machineSubtitle } from './machineSubtitle';

// Use the generated Machine type from GraphQL
type Machine = GeneratedMachine;

interface MachineSidePanelProps {
  /** Whether the panel is open */
  open: boolean;
  /** Callback when the open state changes */
  onOpenChange: (open: boolean) => void;
  /** The machine to display details for */
  machine: Machine | null;
}

/**
 * MachineSidePanel - Side panel for viewing machine details
 *
 * Displays machine information in a slide-out panel. The pane inside picks
 * the tabs: the machine's data tabs around Details and Events.
 *
 * @example
 * ```tsx
 * const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
 *
 * <MachineSidePanel
 *   open={!!selectedMachine}
 *   onOpenChange={(open) => !open && setSelectedMachine(null)}
 *   machine={selectedMachine}
 * />
 * ```
 */
export function MachineSidePanel({
  open,
  onOpenChange,
  machine,
}: MachineSidePanelProps) {
  if (!machine) return null;
  const status = readMachineStatus(machine.state).status;

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={machine.displayName || machine.machineId}
      subtitle={machineSubtitle(machine)}
      // The demo shows the machine's status in the header, as its phone view
      // colours the app bar. Pulse keeps its header until the design says so.
      // Its header takes the status colour, as the phone's app bar does.
      headerClassName={
        IS_DEMO
          ? cn('bg-linear-68', APP_BAR_TONES[getMachineStatusTone(status)])
          : undefined
      }
      headerAction={
        IS_DEMO ? <MachineStatusBadge status={status} /> : undefined
      }
    >
      <MachineDetailPane machine={machine} />
    </SidePanel>
  );
}
